import { applyMiddleware, createStore } from "redux";
import thunk from "redux-thunk";
import reducers from "./reducers";

// Middleware that handles errors globally and ensures safe data access
const safeMiddleware = (store) => (next) => (action) => {
  // Handle async actions (functions)
  if (typeof action === "function") {
    return new Promise((resolve) => {
      try {
        const actionResult = action(
          // Wrap dispatch to catch any errors that might occur during dispatch
          (...args) => {
            try {
              return store.dispatch(...args);
            } catch (error) {
              console.error("Dispatch error:", error);
              return null; // Return null instead of throwing
            }
          },
          store.getState
        );

        // Handle promises returned by async actions
        if (actionResult && typeof actionResult.then === "function") {
          actionResult
            .then((result) => {
              // Ensure the result has a valid structure
              if (result && typeof result === "object") {
                // Handle common Redux patterns: payload.data and data.records
                if (result.payload && typeof result.payload === "object") {
                  result.payload = result.payload || {};
                  result.payload.data = result.payload.data || {};
                  result.payload.records = result.payload.records || [];
                }
                if (result.data && typeof result.data === "object") {
                  result.data = result.data || {};
                  result.data.records = result.data.records || [];
                }
              }
              resolve(result);
            })
            .catch((error) => {
              console.error("Async action error:", error);
              // Return a safe default structure in case of error
              resolve({
                type: action.type || "ASYNC_ERROR",
                data: [],
                error: error.message,
              });
            });
        } else {
          resolve(actionResult);
        }
      } catch (error) {
        console.error("Action execution error:", error);
        resolve(null);
      }
    });
  }

  // Handle synchronous actions
  try {
    const result = next(action);

    // Ensure result has valid payload and data properties
    if (result && typeof result === "object") {
      if (result.payload === null || result.payload === undefined) {
        result.payload = {};
      }
      if (result.data === null || result.data === undefined) {
        result.data = {};
      }
    }

    return result;
  } catch (error) {
    console.error("Sync action error:", error);
    return {
      type: action.type || "SYNC_ERROR",
      data: [],
      error: error.message,
    };
  }
};

// Secure the useSelector hook to handle null/undefined states safely
const secureSelectorHook = () => {
  // Get the original useSelector from ReactRedux
  const originalUseSelector = window.ReactRedux?.useSelector;
  if (originalUseSelector) {
    // Override the useSelector to include error handling
    window.ReactRedux.useSelector = function (selector, ...args) {
      const safeSelector = (state) => {
        try {
          const result = selector(state);
          // Return empty object instead of null/undefined
          return result === null || result === undefined ? {} : result;
        } catch (error) {
          console.warn("Selector error caught:", error);
          return {};
        }
      };
      return originalUseSelector(safeSelector, ...args);
    };
  }
};

// Store configuration function
const configureStore = () => {
  // Apply the secure selector hook
  secureSelectorHook();

  // Configure middleware
  const middleWares = [thunk, safeMiddleware];

  // Create store with empty initial state and middleware
  const store = createStore(reducers, {}, applyMiddleware(...middleWares));

  return store;
};

// Export configured store
export default configureStore();
